<script lang="ts" setup>
import { computed } from 'vue'
import { useIncidentSubmissionStore } from '../../stores/incident-submission'

const incidentStore = useIncidentSubmissionStore()

// TO-DO: Move this to a dedicated endpoint or config store later if this list needs to be dynamic.
// Kept local for now as per efficiency principles.
const knownVendors = [
  { id: 'tesla', label: 'Tesla USB', icon: 'mdi-car' },
  { id: 'garmin', label: 'Garmin', icon: 'mdi-video' },
  { id: 'nextbase', label: 'Nextbase', icon: 'mdi-video' },
  { id: 'blackvue', label: 'BlackVue', icon: 'mdi-video' },
  { id: 'viofo', label: 'VIOFO', icon: 'mdi-video' }
]

// Computed property with a getter/setter for clean v-model usage if needed,
// though we use the method below for explicit clicks.
const selectedVendor = computed({
  get: () => incidentStore.incidentForm.deviceVendor,
  set: (val) => {
    incidentStore.incidentForm.deviceVendor = val
  }
})

function selectVendor(vendorId: string | null) {
  selectedVendor.value = vendorId
}
</script>

<template>
  <div class="row q-col-gutter-y-lg justify-center">
    <div class="col-12">
      <h6 class="text-h6 q-mt-none q-mb-sm text-blue-grey-9">Select your device</h6>
      <p class="text-body2 text-blue-grey-8">
        Identifying your device helps us process your footage.
      </p>
    </div>

    <div class="col-12 col-md-6 col-lg-5">
      <q-card
        v-ripple
        class="cursor-pointer transition-border"
        :class="selectedVendor === null ? 'border-blue bg-blue-1' : 'bg-white'"
        flat
        bordered
        @click="selectVendor(null)"
      >
        <q-card-section class="row items-center no-wrap q-pa-md">
          <q-avatar color="blue-8" text-color="white" icon="mdi-camera-front" class="q-mr-md" />
          <div class="col">
            <div class="text-subtitle1 text-weight-bold text-blue-grey-9">
              Generic / Other Device
            </div>
            <div class="text-caption text-blue-grey-7">Standard phone or unlisted dashcam</div>
          </div>
          <div class="col-auto">
            <q-icon
              v-if="selectedVendor === null"
              name="mdi-check-circle"
              color="blue-8"
              size="sm"
            />
          </div>
        </q-card-section>
      </q-card>
    </div>

    <div class="col-12">
      <div class="row items-center justify-center q-my-sm">
        <q-separator class="col" />
        <div class="col-auto q-px-md text-caption text-uppercase text-weight-bold text-blue-grey-5">
          Or select a specific device
        </div>
        <q-separator class="col" />
      </div>
    </div>

    <div class="col-12">
      <q-scroll-area style="height: 120px" class="full-width" :thumb-style="{ opacity: '0.2' }">
        <div class="row no-wrap q-gutter-x-md q-pb-sm q-px-xs justify-center">
          <q-card
            v-for="vendor in knownVendors"
            :key="vendor.id"
            v-ripple
            flat
            bordered
            class="vendor-card cursor-pointer transition-border text-center flex flex-center"
            :class="selectedVendor === vendor.id ? 'border-blue bg-blue-1' : 'bg-white'"
            @click="selectVendor(vendor.id)"
          >
            <q-card-section>
              <q-icon
                :name="vendor.icon"
                size="lg"
                :color="selectedVendor === vendor.id ? 'blue-8' : 'blue-grey-4'"
                class="transition-color"
              />
              <div
                class="text-subtitle2 q-mt-sm transition-color"
                :class="
                  selectedVendor === vendor.id
                    ? 'text-blue-10 text-weight-bold'
                    : 'text-blue-grey-8'
                "
              >
                {{ vendor.label }}
              </div>
            </q-card-section>
          </q-card>
        </div>
      </q-scroll-area>
    </div>
  </div>
</template>

<style scoped>
.vendor-card {
  width: 140px;
  height: 100px;
  flex-shrink: 0; /* Prevents flexbox from squishing the cards in the scroll area */
}

.transition-border {
  transition: all 0.2s ease-in-out;
}

.transition-color {
  transition: color 0.2s ease-in-out;
}

.border-blue {
  border-color: var(--q-blue-8) !important;
  border-width: 2px !important;
}
</style>
